import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * DrawerShell — reusable right-side drawer with an impressive gradient
 * header. Used to replace centred modals across the app so heavy forms
 * (roster shift editor, booking request detail, patient quick peek,
 * etc.) get the same generous canvas and breathing room.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title, subtitle, eyebrow: strings rendered in the header
 *  - accent: hex colour (defaults to violet)
 *  - icon: ReactElement (rendered in the header chip)
 *  - width: 'sm' | 'md' | 'lg' | 'xl' (default 'md')
 *  - testId: data-testid root
 *  - children: drawer body
 *  - footer: optional footer slot for save/cancel CTA
 */
const W = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export default function DrawerShell({
  open, onClose, title, subtitle, eyebrow,
  accent = '#7c3aed', icon, width = 'md',
  testId = 'drawer', children, footer, headerExtras,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose} />
      <aside
        className={`absolute right-0 top-0 bottom-0 w-full ${W[width] || W.md} bg-white shadow-2xl flex flex-col animate-in slide-in-from-right`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* IMPRESSIVE HEADER */}
        <header
          className="relative px-6 py-5 text-white overflow-hidden flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 60%, ${accent}88 100%)` }}
        >
          {/* Decorative grid pattern */}
          <svg className="absolute inset-0 opacity-[0.12]" width="100%" height="100%" aria-hidden="true">
            <defs>
              <pattern id="drawer-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                <path d="M 24 0 L 0 0 0 24" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#drawer-grid)" />
          </svg>
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-16 left-1/3 w-48 h-48 rounded-full bg-black/10 blur-3xl" />

          <div className="relative flex items-start gap-3">
            {icon && (
              <div className="h-12 w-12 rounded-2xl bg-white/20 ring-2 ring-white/40 backdrop-blur flex items-center justify-center flex-shrink-0 shadow-lg">
                {icon}
              </div>
            )}
            <div className="flex-1 min-w-0">
              {eyebrow && (
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">{eyebrow}</div>
              )}
              {title && (
                <h2 className="text-xl font-extrabold tracking-tight leading-tight" style={{ fontFamily: 'Manrope' }} data-testid={`${testId}-title`}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <div className="text-xs opacity-90 mt-0.5">{subtitle}</div>
              )}
              {headerExtras && <div className="mt-2.5">{headerExtras}</div>}
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 bg-white/15 hover:bg-white/30 ring-1 ring-white/30 transition flex-shrink-0"
              aria-label="Close"
              data-testid={`${testId}-close`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {/* BODY (scrollable) */}
        <div className="flex-1 overflow-y-auto bg-zinc-50/30">{children}</div>

        {/* FOOTER (optional) */}
        {footer && (
          <footer className="px-5 py-3 border-t bg-white flex justify-end gap-2 flex-shrink-0">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}
